import type { PrivacyCenterTrackingPurposePreview } from '@main/cm-types';
import { Skeleton } from '@main/core-ui';
import {
  Card,
  RequiredEnabledAirgap,
  StyledColumnContainer,
} from '@main/privacy-center/src/components';
import { usePartition } from '@main/privacy-center/src/hooks';
import { sp } from '@main/theme';
import { indexBy } from '@main/utils';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { Purpose } from '../../../CommunicationsPreferences/components';
import { privacySettingMessages } from './messages';
import { UnauthenticatedOptOutForm } from './UnauthenticatedOptOutForm';

interface UnauthenticatedSettingsPreviewProps {
  /** current Consent selections */
  consent: Record<string, Purpose>;
  /** Subject Type */
  subjectType: string;
  /** configured purposes */
  purposes: PrivacyCenterTrackingPurposePreview[];
  /** Callback to handle the change of a consent preference */
  onChange: (consent: Record<string, Purpose>) => void;
}

/**
 * Component to preview privacy settings for UnAuthenticated users.
 */
export const UnauthenticatedSettingsPreview: React.FC<
  React.PropsWithChildren<UnauthenticatedSettingsPreviewProps>
> = ({ consent, subjectType, purposes: configuredPurposes, onChange }) => {
  const { formatMessage } = useIntl();
  const [{ partitionOptions, selectedPartition }] = usePartition();

  const regionalizedPurposes = useMemo(
    () =>
      configuredPurposes.filter(
        ({ trackingType }) => consent[trackingType] !== undefined,
      ),
    [configuredPurposes, consent],
  );

  const regionalizedPurposesBySlug = useMemo(
    () => indexBy(regionalizedPurposes, 'trackingType'),
    [regionalizedPurposes],
  );

  // cannot render without a partition
  if (partitionOptions.length === 0) {
    return <RequiredEnabledAirgap />;
  }
  if (!selectedPartition) {
    return <Skeleton />;
  }

  return (
    <>
      {regionalizedPurposes.length > 0 ? (
        <StyledColumnContainer>
          {regionalizedPurposes.map((purpose, index) => (
            <UnauthenticatedOptOutForm
              key={index}
              consent={consent}
              subjectType={subjectType}
              configuredPurpose={
                regionalizedPurposesBySlug[purpose.trackingType]
              }
              onChange={onChange}
            />
          ))}
        </StyledColumnContainer>
      ) : (
        <Card
          description={formatMessage(privacySettingMessages.noRegimePurposes)}
          style={{ padding: sp(2) }}
          disabled
        />
      )}
    </>
  );
};
